// File a scan-type document WITHOUT running extraction — THE one implementation, used by Document Intake (next to
// Scan) and by the Document Scanner's review screen (notices and tax bills). A superseded original bill, a notice the
// extractor can't read: the document is filed and nothing else is recorded.
//
// ⛔ EVERY FIELD EXTRACTION WOULD HAVE SUPPLIED IS SET BY HAND, AND FILING REFUSES UNTIL IT IS (skipBlockers): the
// type, the property and its client, the parcel, and the TAX YEAR — which has NO DEFAULT. The scanner's save falls
// back to the current calendar year, survivable when extraction supplied one and silently wrong when nothing did.
//
// ⛔ NOTHING ELSE IS WRITTEN. No tax_bills, parcel_values, parcel_appeals or parcel_year_data — an empty value row
// would assert that a scan produced nothing, a different and false claim from "no scan was run".
//
// ⛔ THE SKIP IS STORED, NOT DERIVED FROM ABSENCE (extraction_skipped_at / _by): "filed deliberately without
// extraction" and "extraction failed and nobody noticed" leave the same trace otherwise. The optional reason goes in
// the activity log (action 'file_without_scanning'), which the "not scanned" chip reads for its tooltip.
//
// Two modes:
//   existingId — the document is already in Wigley (an Intake row): that row is FILED in place. No upload, no second
//                row. (The scanner used to insert a new row here, leaving the Intake original unfiled in the pile.)
//   file       — a fresh upload in the scanner: uploaded to R2 first, then inserted (no row for bytes not in R2).
import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { buildFilingUpdates } from '../constants/docFiling';
import { uploadFile } from './uploadFile';

export const SKIP_ACTION = 'file_without_scanning';
export const isTaxYear = (y) => /^(19|20)\d{2}$/.test(String(y ?? '').trim());

// What is still missing, in words ([] = ready). Named, not a silently disabled button.
export function skipBlockers({ hasFile, vocab, typeKey, year, property, parcel }) {
  const out = [];
  if (!hasFile) out.push('a file');
  if (!typeKey || !vocab?.type(typeKey)) out.push('a document type');
  if (!isTaxYear(year)) out.push('a 4-digit tax year');
  if (!property) out.push('a property');
  if (property && !property.client_id) out.push('a client on that property');
  if (!parcel) out.push('a parcel');
  return out;
}

// → { ok:true, id, folderName, warning? } or { ok:false, error } (error = what happened; nothing half-written
// except where `warning` says so).
export async function fileWithoutScanning({
  vocab, typeKey, property, parcel, year, staffName, reason, extractionError,
  existingId, file, viewableClient = false, viewableConsultant = true, source,
}) {
  const blockers = skipBlockers({ hasFile: !!(existingId || file), vocab, typeKey, year, property, parcel });
  if (blockers.length) return { ok: false, error: `Set ${blockers.join(', ')} first. Nothing was filed.` };
  const y = String(year).trim();
  const type = vocab.type(typeKey);
  const by = staffName || 'NTRG Staff';
  const note = String(reason || '').trim() || null;

  // The shared filing model; resolved BEFORE any upload, so a refused type leaves no orphaned object in R2.
  let filing;
  try {
    filing = buildFilingUpdates({
      vocab, docType: type.key, clientId: property.client_id, propertyId: property.id, parcelId: parcel.id, year: y,
      viewableClient, viewableConsultant, existingPropertyId: property.id, staffName: by,
    });
  } catch (e) { return { ok: false, error: `${e.message} Nothing was filed.` }; }
  const { updates, folder, folderName, docFields } = filing;
  const skipped = { extraction_skipped_at: new Date().toISOString(), extraction_skipped_by: by };
  const acct = parcel.account_number || null;

  let id;
  if (existingId) {
    // ⛔ .select('id') for the affected-row count: file_exchange is RLS-enabled, and a blocked update returns 0 rows
    // and no error.
    const { data, error } = await supabase.from('file_exchange').update({ ...updates, ...skipped }).eq('id', existingId).select('id');
    if (error) return { ok: false, error: `Filing failed — ${error.message}` };
    if (!data?.length) return { ok: false, error: 'Filing failed — the update affected 0 rows (blocked, or the document no longer exists).' };
    id = existingId;
  } else {
    const fileName = file.name || `${type.name}_${property.name}_${y}.pdf`;
    const safeAcct = String(acct || '').replace(/[^a-zA-Z0-9_-]/g, '_');
    const storagePath = `${property.client_id}/mpid-${property.master_property_id}/${folder}/${y}/${safeAcct}_${Date.now()}_${fileName}`;
    const { error: ue } = await uploadFile(storagePath, file);
    if (ue) return { ok: false, error: `The file failed to upload — nothing was filed. ${ue.message}` };
    const { data, error } = await supabase.from('file_exchange').insert({
      ...updates, ...skipped,
      client_id: property.client_id, account_number: acct, master_property_id: String(property.master_property_id || ''),
      uploaded_by_name: by, direction: 'ntrg_internal', file_name: fileName, file_size_bytes: file.size || 0, storage_path: storagePath,
    }).select('id').single();
    if (error || !data?.id) return { ok: false, error: `Filing failed — ${error ? error.message : 'no row was created'}` };
    id = data.id;
  }

  const warnings = [];
  // Parcel-level mirror, so the document shows on the parcel as well as the property.
  const { error: pdErr } = await supabase.from('parcel_documents').upsert({
    parcel_id: parcel.id, file_exchange_id: id, ...docFields, year_subfolder: y, property_id: property.id,
    master_property_id: property.master_property_id, account_number: acct,
  }, { onConflict: 'file_exchange_id' });
  if (pdErr) warnings.push(`it may not show on the parcel (${pdErr.message})`);

  const { error: logErr } = await supabase.from('activity_log').insert({
    staff_user: by, action: SKIP_ACTION, entity_type: 'file_exchange', entity_id: id,
    entity_name: `${type.name} · ${property.name} · ${y}`, property_name: property.name || null,
    details: { parcel_id: parcel.id, folder, type_key: type.key, tax_year: y, reason: note, extraction_error: extractionError || null, source: source || null },
  });
  if (logErr) warnings.push(`the activity-log entry${note ? ' (and its reason note)' : ''} was not saved (${logErr.message})`);

  return { ok: true, id, folderName, warning: warnings.length ? `Filed, but ${warnings.join('; ')}.` : null };
}

// For the "not scanned" chip's tooltip: file_exchange id → { reason, by, at } from the activity log.
// Chunked so a long list never builds an over-long URL.
export async function loadSkipNotes(ids) {
  const out = {};
  const list = [...new Set((ids || []).filter(Boolean))];
  for (let i = 0; i < list.length; i += 100) {
    const { data, error } = await supabase.from('activity_log')
      .select('entity_id, staff_user, created_at, details')
      .eq('action', SKIP_ACTION).in('entity_id', list.slice(i, i + 100)).order('created_at', { ascending: true });
    if (error) { console.warn('[loadSkipNotes]', error.message); continue; }
    for (const r of data || []) out[r.entity_id] = { reason: r.details?.reason || null, by: r.staff_user, at: r.created_at };   // latest wins
  }
  return out;
}

// React: the skip notes for whichever of `files` carry extraction_skipped_at (re-loads when that set changes).
export function useSkipNotes(files) {
  const [notes, setNotes] = useState({});
  const key = (files || []).filter(f => f.extraction_skipped_at).map(f => f.id).join(',');
  useEffect(() => {
    let alive = true;
    if (!key) { setNotes({}); return undefined; }
    loadSkipNotes(key.split(',')).then(n => { if (alive) setNotes(n); });
    return () => { alive = false; };
  }, [key]);
  return notes;
}

// The chip's tooltip text.
export function skipTooltip(note) {
  const base = 'Filed without running extraction — no values were recorded from this document';
  if (!note) return base;
  const when = note.at ? new Date(note.at).toLocaleDateString() : '';
  return `${base}.\nBy ${note.by || 'staff'}${when ? ` on ${when}` : ''}${note.reason ? `\nReason: ${note.reason}` : ''}`;
}
